import { useEffect, useRef } from 'react'
import { Bot } from 'lucide-react'
import { ScrollArea } from '@/components/ui'
import { Message } from './Message'
import type { ChatMessage } from '@/stores'

interface MessageListProps {
  messages: ChatMessage[]
  isLoading?: boolean
}

export function MessageList({ messages, isLoading }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="h-12 w-12 bg-gradient-to-br from-primary/20 to-[hsl(280,100%,70%)]/20 rounded-full flex items-center justify-center mb-4">
          <Bot className="h-6 w-6 text-primary" />
        </div>
        <h3 className="font-medium mb-1 bg-gradient-to-r from-primary to-[hsl(280,100%,70%)] bg-clip-text text-transparent">CA Copilot</h3>
        <p className="text-sm text-muted-foreground max-w-[250px]">
          Your digital chief of staff. Ask about statutory deadlines, task assignments, time logs, or client work.
        </p>
      </div>
    )
  }

  return (
    <ScrollArea className="flex-1">
      <div className="py-4">
        {messages.map((message) => (
          <Message key={message.id} message={message} />
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex gap-1.5 px-4 py-3">
            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-primary/20 to-[hsl(280,100%,70%)]/20 flex items-center justify-center">
              <Bot className="h-4 w-4 text-primary" />
            </div>
            <div className="flex items-center gap-1 px-4 py-2.5 bg-muted/30 rounded-2xl rounded-bl-md">
              <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:0ms]" />
              <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:150ms]" />
              <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  )
}
